
import React, { useLayoutEffect, useRef, useState, useMemo, useEffect } from 'react';

interface User {
  uid: string;
  name: string;
  email: string;
  picture: string;
  monthlySpendCap?: number;
  topupBalance?: number;
  currentUsage?: number;
  hasPaymentMethod?: boolean;
  primaryPaymentMethod?: 'stripe' | 'google-pay' | null;
  notifications?: {
    pushEnabled: boolean;
    emailEnabled: boolean;
    newArticle: boolean;
    newFeedPost: boolean;
    newFeedPostInGroup: boolean;
    commentsOnMyPost: boolean;
    responsesToMyComment: boolean;
  };
}

import { Bookmark, BookmarkCategory } from '../types';
import { Invoice, PaymentRecord, saveInvoice, saveUserSettings } from '../services/firestoreService';
import GooglePayButton from '@google-pay/button-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer, 
  Cell 
} from 'recharts';
import { Receipt, CreditCard, ChevronLeft, CheckCircle2, Clock, DollarSign, Wallet } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { uploadMedia, getResizedImageUrl, getSafeDownloadUrl } from '../services/storageService';

interface SettingsPageProps {
  user: User;
  aiUsage?: { dailyFreeUsed: number; freeLimit: number; credits: number };
  theme: 'light' | 'dark' | 'system';
  fontSize: 'sm' | 'base' | 'lg';
  onThemeChange: (theme: 'light' | 'dark' | 'system') => void;
  onFontSizeChange: (size: 'sm' | 'base' | 'lg') => void;
  onClose: () => void;
  onSignOut: () => void;
  bookmarks: Bookmark[];
  bookmarkCategories: BookmarkCategory[];
  payments: PaymentRecord[];
  onNavigateToBookmark: (bookmark: Bookmark) => void;
  onDeleteBookmark: (id: string) => void;
  languages: { code: string; name: string; flag: string }[];
  currentLanguageCode: string;
  onLanguageChange: (code: string) => void;
  onSpendCapChange: (cap: number) => void;
  payAsYouGo?: boolean;
  onPayAsYouGoChange: (payAsYouGo: boolean) => void;
  quotaAlertThreshold?: number;
  onQuotaAlertThresholdChange: (threshold: number) => void;
  onPaymentMethodUpdate: (method: 'stripe' | 'google-pay' | null) => void;
  onTopup: (amount: number, method?: string) => void;
  onUpdateProfile: (updates: { displayName?: string, photoURL?: string, notifications?: any }) => Promise<void>;
  onChangePassword?: () => void;
  hash?: string;
  isEditorMode?: boolean;
  bypassBilling?: boolean;
  onBypassBillingChange?: (val: boolean) => void;
}

const GoogleG = ({ size = 24, className = "" }: { size?: number, className?: string }) => (
    <span 
        className={`bg-white rounded-md flex items-center justify-center shadow-sm border border-stone-100 overflow-hidden ${className}`} 
        style={{ width: size, height: size, display: 'inline-flex' }}
    >
        <img 
            src="https://www.gstatic.com/images/branding/googleg/1x/googleg_standard_color_64dp.png" 
            alt="Google" 
            style={{ width: size * 0.6, height: size * 0.6 }}
            className="pointer-events-none" 
            referrerPolicy="no-referrer" 
        />
    </span>
);

const GPayLogo = ({ className = "", theme = 'light', showText = true }: { className?: string, theme?: 'light' | 'dark' | 'system', showText?: boolean }) => {
    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <GoogleG size={24} />
            {showText && (
                <span className={`font-bold text-sm tracking-tight ${theme === 'dark' ? 'text-white' : 'text-[#3C4043]'}`}>Pay</span>
            )}
        </div>
    );
};

const SettingsPage: React.FC<SettingsPageProps> = ({
  user,
  aiUsage = { dailyFreeUsed: 0, freeLimit: 10, credits: 0 },
  theme,
  fontSize,
  onThemeChange,
  onFontSizeChange,
  onSignOut,
  bookmarks,
  bookmarkCategories,
  onNavigateToBookmark,
  onDeleteBookmark,
  languages,
  currentLanguageCode,
  onLanguageChange,
  onSpendCapChange,
  payAsYouGo = false,
  onPayAsYouGoChange,
  quotaAlertThreshold = 5,
  onQuotaAlertThresholdChange,
  onPaymentMethodUpdate,
  onTopup,
  onUpdateProfile,
  onChangePassword,
  payments,
  hash,
  isEditorMode = false,
  bypassBilling = false,
  onBypassBillingChange
}) => {
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [showPaymentSubpage, setShowPaymentSubpage] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'stripe' | 'google-pay'>(() => {
    if (user.primaryPaymentMethod === 'stripe' || user.primaryPaymentMethod === 'google-pay') {
        return user.primaryPaymentMethod;
    }
    return 'google-pay';
  });
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [successType, setSuccessType] = useState<'setup' | 'topup'>('setup');
  const [showInvoicesSubpage, setShowInvoicesSubpage] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [stripeCardNum, setStripeCardNum] = useState('');
  const [stripeExpiry, setStripeExpiry] = useState('');
  const [stripeCvc, setStripeCvc] = useState('');

  const [topupPreset, setTopupPreset] = useState<number | 'custom'>(10);
  const [customTopupValue, setCustomTopupValue] = useState<string>('50');

  const effectiveTopupAmount = useMemo(() => {
    if (topupPreset === 'custom') {
      const val = parseFloat(customTopupValue);
      return isNaN(val) || val <= 0 ? 5 : Number(val.toFixed(2));
    }
    return topupPreset;
  }, [topupPreset, customTopupValue]);

  useEffect(() => {
    if (user.primaryPaymentMethod === 'stripe' || user.primaryPaymentMethod === 'google-pay') {
        setSelectedPaymentMethod(user.primaryPaymentMethod);
    }
  }, [user.primaryPaymentMethod]);

  const [isSendingBill, setIsSendingBill] = useState(false);

  const usageData = useMemo(() => {
    // Only show current month for now
    return [{
      month: new Date().toLocaleString('default', { month: 'short' }),
      usage: user.currentUsage || 0,
      status: 'pending' // current is generally pending
    }];
  }, [user.currentUsage]);


  // [Removed handleSendMockBill and currentPendingInvoice]

  // [Removed handleBalanceMonth as it relied on mock invoices]

  // Logic to open sub-page if hash is present
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
        setSuccessType('topup');
        setShowSuccessScreen(true);
        // Clean up URL
        const newUrl = window.location.pathname + window.location.search.replace(/[?&]payment=success/, '').replace(/^&/, '?');
        window.history.replaceState({}, '', newUrl);
    }

    if (hash === '#payment-methods') {
      setShowPaymentSubpage(true);
    } else if (hash === '#usage') {
        const el = document.getElementById('usage-section');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hash]);

  const categories = useMemo(() => {
    const paths = new Set(bookmarks.map(b => b.categoryPath));
    return ['All', ...Array.from(paths)].sort();
  }, [bookmarks]);

  const filteredBookmarks = useMemo(() => {
    if (activeCategory === 'All') return bookmarks;
    return bookmarks.filter(b => b.categoryPath === activeCategory);
  }, [bookmarks, activeCategory]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingAvatar(true);
    try {
        const path = `avatars/${user.uid}/${Date.now()}_${file.name}`;
        const storagePath = await uploadMedia(file, path);
        const photoURL = await getSafeDownloadUrl(storagePath);
        await onUpdateProfile({ photoURL });
    } catch (err) {
        console.error("Avatar upload failed", err);
    } finally {
        setIsUploadingAvatar(false);
    }
  };

  return (
    <div className="w-full h-full overflow-y-auto overflow-x-hidden custom-scrollbar bg-[#fbfaf6] dark:bg-stone-900 transition-colors duration-300">
      <div className="max-w-3xl mx-auto p-6 lg:p-10 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
        <div className="mb-2">
            <h2 className="text-3xl font-serif font-bold text-stone-800 dark:text-stone-100">Preferences</h2>
            <p className="text-stone-500 dark:text-stone-400 mt-1 text-sm">Customize your experience within the archives.</p>
        </div>

        {/* Appearance Card */}
        <div className="bg-white dark:bg-stone-800 rounded-2xl p-6 shadow-sm border border-stone-200 dark:border-stone-700">
            <h3 className="font-bold text-stone-800 dark:text-stone-200 mb-6 flex items-center gap-2">
                <i className="fa-solid fa-palette text-amber-500"></i>
                Appearance
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Theme Selector */}
                <div>
                    <label className="block text-xs font-bold text-stone-400 uppercase mb-3">Theme</label>
                    <div className="flex gap-2">
                        {(['light', 'dark', 'system'] as const).map(t => (
                            <button 
                                key={t}
                                onClick={() => onThemeChange(t)}
                                className={`flex-1 py-3 px-2 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${
                                theme === t 
                                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100' 
                                : 'border-stone-100 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-700/50 text-stone-600 dark:text-stone-400'
                                }`}
                            >
                                <i className={`fa-solid ${t === 'light' ? 'fa-sun' : t === 'dark' ? 'fa-moon' : 'fa-desktop'} text-lg`}></i>
                                <span className="font-semibold text-xs capitalize">{t}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Font Size Selector - Unified Layout */}
                <div>
                     <label className="block text-xs font-bold text-stone-400 uppercase mb-3">Font Size</label>
                     <div className="flex gap-2">
                        {(['sm', 'base', 'lg'] as const).map(s => (
                            <button
                                key={s}
                                onClick={() => onFontSizeChange(s)}
                                className={`flex-1 py-3 px-2 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${
                                fontSize === s 
                                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100' 
                                : 'border-stone-100 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-700/50 text-stone-600 dark:text-stone-400'
                                }`}
                            >
                                <div className="h-5 flex items-center justify-center">
                                    <span className={`font-serif leading-none ${s === 'sm' ? 'text-xs' : s === 'lg' ? 'text-xl' : 'text-base'}`}>Aa</span>
                                </div>
                                <span className="font-semibold text-xs capitalize">{s === 'base' ? 'Medium' : s === 'sm' ? 'Small' : 'Large'}</span>
                            </button>
                        ))}
                     </div>
                </div>
            </div>
        </div>

        {/* Account & Language Card */}
        <div className="bg-white dark:bg-stone-800 rounded-2xl p-6 shadow-sm border border-stone-200 dark:border-stone-700">
            <h3 className="font-bold text-stone-800 dark:text-stone-200 mb-6 flex items-center gap-2">
                <i className="fa-solid fa-user-gear text-amber-500"></i>
                Account & Language
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Account Section */}
                <div className="space-y-4">
                    <label className="block text-xs font-bold text-stone-400 uppercase tracking-widest">Account Info</label>
                    <div className="flex items-center gap-4 p-4 bg-stone-50 dark:bg-stone-900/50 rounded-xl border border-stone-100 dark:border-stone-700">
                        <div className="relative group cursor-pointer" onClick={() => avatarInputRef.current?.click()}>
                            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white dark:border-stone-800 shadow-sm relative">
                                <img src={user.picture} className="w-full h-full object-cover" alt={user.name} />
                                {isUploadingAvatar && (
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                        <i className="fa-solid fa-circle-notch fa-spin text-white"></i>
                                    </div>
                                )}
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center border-2 border-white dark:border-stone-800 text-white shadow-sm group-hover:scale-110 transition-transform">
                                <i className="fa-solid fa-camera text-[10px]"></i>
                            </div>
                            <input 
                                type="file" 
                                ref={avatarInputRef} 
                                onChange={handleAvatarChange} 
                                className="hidden" 
                                accept="image/*"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-stone-800 dark:text-stone-100 truncate">{user.name}</p>
                            <p className="text-xs text-stone-500 truncate">{user.email}</p>
                        </div>
                    </div>
                    
                    {onChangePassword && (
                        <button 
                            onClick={onChangePassword}
                            className="w-full py-2.5 rounded-lg border border-amber-200 dark:border-amber-900/30 text-amber-600 dark:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/10 font-bold text-xs transition-colors flex items-center justify-center gap-2"
                        >
                            <i className="fa-solid fa-key"></i>
                            Change Password
                        </button>
                    )}

                    <button 
                        onClick={onSignOut}
                        className="w-full py-2.5 rounded-lg border border-red-200 dark:border-red-900/30 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 font-bold text-xs transition-colors flex items-center justify-center gap-2"
                    >
                        <i className="fa-solid fa-right-from-bracket"></i>
                        Sign Out
                    </button>
                </div>

                {/* Language Section */}
                <div className="space-y-4">
                    <label className="block text-xs font-bold text-stone-400 uppercase tracking-widest">Language Setting</label>
                    <LanguageSelector 
                        languages={languages}
                        currentLangCode={currentLanguageCode}
                        onSelect={onLanguageChange}
                        fullWidth={true}
                        triggerClassName="w-full bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl px-4 py-3 flex items-center justify-between text-stone-700 dark:text-stone-200 shadow-sm hover:border-amber-500/50 transition-all font-medium text-sm"
                    />
                </div>
            </div>
        </div>

        {/* Notifications Card */}
        <div className="bg-white dark:bg-stone-800 rounded-2xl p-6 shadow-sm border border-stone-200 dark:border-stone-700">
            <h3 className="font-bold text-stone-800 dark:text-stone-200 mb-6 flex items-center gap-2">
                <i className="fa-solid fa-bell text-amber-500"></i>
                Notifications
            </h3>
            
            <div className="space-y-6">
                <p className="text-xs text-stone-500 italic">Emails will be sent by <span className="font-mono text-stone-600 dark:text-stone-300">info@edgarcayce.ch</span>.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Delivery Methods */}
                    <div className="space-y-4">
                        <label className="block text-xs font-bold text-stone-400 uppercase tracking-widest">Delivery Methods</label>
                        
                        <div className="space-y-3 p-4 bg-stone-50 dark:bg-stone-900/50 rounded-xl border border-stone-100 dark:border-stone-700">
                            <label className="flex items-center justify-between cursor-pointer group">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 flex items-center justify-center">
                                        <i className="fa-solid fa-mobile-screen"></i>
                                    </div>
                                    <span className="text-sm font-bold text-stone-700 dark:text-stone-300">Push Notifications</span>
                                </div>
                                <div className={`w-10 h-6 rounded-full transition-colors relative ${(user.notifications?.pushEnabled ?? true) ? 'bg-amber-500' : 'bg-stone-200 dark:bg-stone-700'}`}>
                                    <input 
                                        type="checkbox" 
                                        checked={user.notifications?.pushEnabled ?? true}
                                        onChange={async (e) => {
                                            const enabled = e.target.checked;
                                            if (enabled && 'Notification' in window) {
                                                const permission = await Notification.requestPermission();
                                                if (permission !== 'granted') {
                                                    alert('Push notification permission denied.');
                                                    return;
                                                }
                                            }
                                            onUpdateProfile({ notifications: { ...user.notifications, pushEnabled: enabled } });
                                        }}
                                        className="sr-only"
                                    />
                                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${(user.notifications?.pushEnabled ?? true) ? 'translate-x-4' : ''}`} />
                                </div>
                            </label>
                            
                            <label className="flex items-center justify-between cursor-pointer group pt-2 border-t border-stone-200 dark:border-stone-700">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center">
                                        <i className="fa-solid fa-envelope"></i>
                                    </div>
                                    <span className="text-sm font-bold text-stone-700 dark:text-stone-300">Email Notifications</span>
                                </div>
                                <div className={`w-10 h-6 rounded-full transition-colors relative ${(user.notifications?.emailEnabled ?? true) ? 'bg-amber-500' : 'bg-stone-200 dark:bg-stone-700'}`}>
                                    <input 
                                        type="checkbox" 
                                        checked={user.notifications?.emailEnabled ?? true}
                                        onChange={(e) => onUpdateProfile({ notifications: { ...user.notifications, emailEnabled: e.target.checked } })}
                                        className="sr-only"
                                    />
                                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${(user.notifications?.emailEnabled ?? true) ? 'translate-x-4' : ''}`} />
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Event Triggers */}
                    <div className="space-y-4">
                        <label className="block text-xs font-bold text-stone-400 uppercase tracking-widest">Notify me when...</label>
                        
                        <div className="space-y-3 p-4 bg-stone-50 dark:bg-stone-900/50 rounded-xl border border-stone-100 dark:border-stone-700">
                            {[
                                { id: 'newArticle', label: 'New article release' },
                                { id: 'newFeedPost', label: 'New feed post' },
                                { id: 'newFeedPostInGroup', label: '— In one of my groups' },
                                { id: 'commentsOnMyPost', label: 'Comments on my posts' },
                                { id: 'responsesToMyComment', label: 'Responses to my comments' },
                            ].map((evt) => (
                                <label key={evt.id} className={`flex items-center justify-between cursor-pointer group ${evt.id === 'newFeedPostInGroup' ? 'pl-6' : ''}`}>
                                    <span className="text-sm font-medium text-stone-700 dark:text-stone-300">{evt.label}</span>
                                    <div className={`w-8 h-5 rounded-full transition-colors relative ${(user.notifications?.[evt.id as keyof typeof user.notifications] ?? true) ? 'bg-amber-500' : 'bg-stone-200 dark:bg-stone-700'}`}>
                                        <input 
                                            type="checkbox" 
                                            checked={user.notifications?.[evt.id as keyof typeof user.notifications] ?? true}
                                            onChange={(e) => onUpdateProfile({ notifications: { ...user.notifications, [evt.id]: e.target.checked } })}
                                            className="sr-only"
                                        />
                                        <div className={`absolute top-1 left-1 w-3 h-3 bg-white rounded-full transition-transform ${(user.notifications?.[evt.id as keyof typeof user.notifications] ?? true) ? 'translate-x-3' : ''}`} />
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* AI Usage & Spending Card */}
        {(!bypassBilling || isEditorMode) && (
        <div id="usage-section" className="bg-white dark:bg-stone-800 rounded-2xl p-6 shadow-sm border border-stone-200 dark:border-stone-700">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-2 border-b border-stone-100 dark:border-stone-700/50">
                <h3 className="font-bold text-stone-800 dark:text-stone-200 flex items-center gap-2 text-lg">
                    <i className="fa-solid fa-brain text-amber-500"></i>
                    AI Intelligence & Quota
                </h3>

                {isEditorMode && (
                    <label className="inline-flex items-center gap-2.5 cursor-pointer p-2 px-3.5 rounded-xl bg-amber-500/10 dark:bg-amber-900/30 border border-amber-500/30 dark:border-amber-700/50 hover:bg-amber-500/20 transition-all shadow-sm">
                        <span className="text-xs font-bold text-amber-900 dark:text-amber-200 flex items-center gap-1.5 select-none">
                            <i className="fa-solid fa-bolt-lightning text-amber-500"></i>
                            Bypass Billing & Limits (Free for All)
                        </span>
                        <div 
                            onClick={(e) => { e.preventDefault(); onBypassBillingChange?.(!bypassBilling); }}
                            className={`w-11 h-6 rounded-full relative transition-colors ${bypassBilling ? 'bg-amber-500' : 'bg-stone-300 dark:bg-stone-700'}`}
                        >
                            <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${bypassBilling ? 'left-6' : 'left-1'}`}></div>
                        </div>
                    </label>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
                {/* 10 Free Daily Messages */}
                <div className="p-4 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-100 dark:border-amber-900/30 h-full">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <span className="text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest block mb-1">Daily Free Allowance</span>
                            <h4 className="text-sm font-bold text-stone-800 dark:text-stone-100">10 Free Messages / Day</h4>
                        </div>
                        <div className="text-right">
                             <span className="text-2xl font-serif font-bold text-amber-600 dark:text-amber-400">{aiUsage.freeLimit - aiUsage.dailyFreeUsed}</span>
                             <span className="text-xs text-stone-500 block">Remaining Today</span>
                        </div>
                    </div>
                    <div className="h-2 w-full bg-stone-100 dark:bg-stone-800 rounded-full overflow-hidden mb-2">
                        <div 
                            className="h-full bg-amber-500 transition-all duration-700" 
                            style={{ width: `${Math.max(0, Math.min(100, (aiUsage.dailyFreeUsed / aiUsage.freeLimit) * 100))}%` }}
                        />
                    </div>
                    {aiUsage.credits > 0 && (
                        <div className="mt-3 pt-3 border-t border-amber-500/10 flex items-center justify-between">
                            <span className="text-[10px] font-bold text-stone-500 uppercase tracking-widest">Bonus Credits</span>
                            <span className="text-xs font-bold text-amber-600 dark:text-amber-400">{aiUsage.credits} Messages</span>
                        </div>
                    )}
                    <p className="text-[10px] text-stone-500 italic mt-2">Resets every midnight. These messages use the standard high-speed intelligence model.</p>
                </div>
                {!payAsYouGo && (
                    <div className="flex items-center justify-between animate-in fade-in slide-in-from-top-2 duration-300 h-full">
                        <div>
                            <p className="text-sm font-bold text-stone-800 dark:text-stone-100 uppercase tracking-wider">Monthly Spending Cap</p>
                            <p className="text-xs text-stone-500 mt-1 italic">Control your project's AI compute allocation.</p>
                        </div>
                        <div className="flex items-center gap-2 bg-stone-50 dark:bg-stone-900 px-3 py-2 rounded-xl border border-stone-200 dark:border-stone-700 shadow-inner">
                            <span className="text-stone-400 text-xs font-bold">$</span>
                            <input 
                                type="number"
                                value={user.monthlySpendCap || 0}
                                onChange={(e) => onSpendCapChange(Number(e.target.value))}
                                className="w-16 bg-transparent outline-none text-right font-mono font-bold text-amber-600 dark:text-amber-400"
                                min="0"
                            />
                        </div>
                    </div>
                )}

                <div className="pt-2">
                    <label className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-10 h-6 rounded-full transition-colors relative ${payAsYouGo ? 'bg-amber-500' : 'bg-stone-200 dark:bg-stone-700'}`}>
                            <input 
                                type="checkbox" 
                                checked={payAsYouGo}
                                onChange={(e) => onPayAsYouGoChange(e.target.checked)}
                                className="sr-only"
                            />
                            <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${payAsYouGo ? 'translate-x-4' : ''}`} />
                        </div>
                        <div>
                            <span className="text-stone-700 dark:text-stone-300 font-bold text-sm block">Pay as you go</span>
                            <span className="text-[10px] text-stone-500 block">Charge automatically when spending cap is reached (Gemini 2.5 Flash: $0.075/1M input & $0.30/1M output tokens, approx. $0.0002/prompt)</span>
                        </div>
                    </label>
                </div>

                {/* AI Model Rates & Cost Breakdown */}
                <div className="col-span-1 md:col-span-2 pt-4 border-t border-stone-200 dark:border-stone-700">
                    <div className="bg-stone-50 dark:bg-stone-900/60 p-4 rounded-xl border border-stone-200/80 dark:border-stone-700/60">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                <span className="text-xs font-bold text-stone-800 dark:text-stone-200 uppercase tracking-wider">Billed Model:</span>
                                <span className="text-xs font-mono font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-2.5 py-0.5 rounded-full border border-amber-200 dark:border-amber-700">
                                    Gemini 2.5 Flash
                                </span>
                            </div>
                            <span className="text-[11px] font-semibold text-stone-500 dark:text-stone-400">
                                Token Compute Rates
                            </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                            <div className="p-2.5 bg-white dark:bg-stone-800 rounded-lg border border-stone-200/60 dark:border-stone-700/60">
                                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest block mb-0.5">Input Tokens</span>
                                <span className="font-mono font-bold text-stone-800 dark:text-stone-200">$0.075</span>
                                <span className="text-[10px] text-stone-500 block">per 1M tokens</span>
                            </div>
                            <div className="p-2.5 bg-white dark:bg-stone-800 rounded-lg border border-stone-200/60 dark:border-stone-700/60">
                                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest block mb-0.5">Output Tokens</span>
                                <span className="font-mono font-bold text-stone-800 dark:text-stone-200">$0.300</span>
                                <span className="text-[10px] text-stone-500 block">per 1M tokens</span>
                            </div>
                            <div className="p-2.5 bg-amber-500/10 dark:bg-amber-900/20 rounded-lg border border-amber-500/20 dark:border-amber-700/30">
                                <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest block mb-0.5">Approx. Cost / Prompt</span>
                                <span className="font-mono font-bold text-amber-700 dark:text-amber-300">~$0.00015 – $0.0004</span>
                                <span className="text-[10px] text-stone-500 block">~2,500–5,000 prompts / $1.00</span>
                            </div>
                        </div>
                    </div>
                </div>

                {!payAsYouGo && (
                    <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-stone-400">
                            <span>Current Usage</span>
                            <span>${(user.currentUsage || 0).toFixed(2)} / ${((user.monthlySpendCap || 0) + (user.topupBalance || 0)).toFixed(2)}</span>
                        </div>
                        <div className="h-2 w-full bg-stone-100 dark:bg-stone-700 rounded-full overflow-hidden shadow-inner">
                            <div 
                                className={`h-full transition-all duration-500 ${
                                    (user.currentUsage || 0) >= ((user.monthlySpendCap || 0) + (user.topupBalance || 0)) 
                                    ? 'bg-red-500' 
                                    : (user.currentUsage || 0) > ((user.monthlySpendCap || 0) + (user.topupBalance || 0)) * 0.8 
                                        ? 'bg-amber-500' 
                                        : 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.3)]'
                                }`}
                                style={{ width: `${Math.min(100, ((user.currentUsage || 0) / Math.max(0.01, (user.monthlySpendCap || 0) + (user.topupBalance || 0))) * 100)}%` }}
                            />
                        </div>
                        {user.topupBalance && user.topupBalance > 0 ? (
                            <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-amber-500 mt-1">
                                <span>Prepaid Credit</span>
                                <span>+${user.topupBalance.toFixed(2)}</span>
                            </div>
                        ) : null}
                        <p className="text-[10px] text-stone-400 italic"> Usage resets on the 1st of every month. You will only be billed for your individual compute time.</p>
                    </div>
                )}
                
                {/* Quota Alerts */}
                <div className="pt-4 border-t border-stone-200 dark:border-stone-700">
                     <p className="font-bold text-stone-800 dark:text-stone-100 uppercase tracking-wider text-sm mb-4">Quota Alerts</p>
                     
                     <div className="bg-stone-50 dark:bg-stone-900/50 p-4 rounded-xl space-y-4">
                         <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-bold text-stone-400 uppercase">Alert Threshold</p>
                                <p className="text-[10px] text-stone-500 mt-0.5">Notification sent to: <span className="text-stone-700 dark:text-stone-300 font-mono">{user.email}</span></p>
                            </div>
                            <div className="flex items-center gap-2 bg-white dark:bg-stone-800 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 shadow-sm">
                                <span className="text-stone-400 text-xs">$</span>
                                <input 
                                    type="number"
                                    value={quotaAlertThreshold}
                                    onChange={(e) => onQuotaAlertThresholdChange(Number(e.target.value))}
                                    min="1"
                                    className="w-14 bg-transparent outline-none text-right text-xs font-mono font-bold text-stone-700 dark:text-stone-200"
                                />
                            </div>
                         </div>
                     </div>
                 </div>
                
                {/* Top Up Section */}
                {!payAsYouGo && (
                    <div className="pt-4 border-t border-stone-200 dark:border-stone-700 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-bold text-stone-800 dark:text-stone-100 uppercase tracking-wider">Top Up Quota</p>
                            {user.primaryPaymentMethod === 'google-pay' && (
                                <span className="text-[10px] font-bold text-stone-500 dark:text-stone-400 flex items-center gap-1 uppercase tracking-wider">
                                    <GoogleG size={12} /> Google Pay Active
                                </span>
                            )}
                        </div>

                        {/* Dynamic Billing Amount Selector: 5 / 10 / 20 / Custom */}
                        <div className="space-y-2 mb-3">
                            <div className="flex items-center gap-1.5 p-1 bg-stone-100 dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-700">
                                {[5, 10, 20].map((amount) => (
                                    <button
                                        key={amount}
                                        type="button"
                                        onClick={() => setTopupPreset(amount)}
                                        className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition-all ${
                                            topupPreset === amount
                                                ? 'bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 shadow-sm border border-stone-200 dark:border-stone-700'
                                                : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-200'
                                        }`}
                                    >
                                        ${amount}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => setTopupPreset('custom')}
                                    className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition-all ${
                                        topupPreset === 'custom'
                                            ? 'bg-white dark:bg-stone-800 text-amber-600 dark:text-amber-400 shadow-sm border border-amber-300 dark:border-amber-600'
                                            : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-200'
                                    }`}
                                >
                                    Custom
                                </button>
                            </div>

                            {topupPreset === 'custom' && (
                                <div className="flex items-center gap-2 bg-stone-50 dark:bg-stone-900/60 p-2.5 rounded-xl border border-stone-200 dark:border-stone-700 animate-in fade-in slide-in-from-top-1 duration-200">
                                    <span className="text-xs font-bold text-stone-400 pl-1 uppercase tracking-wider">Amount:</span>
                                    <div className="relative flex-1 flex items-center">
                                        <span className="absolute left-3 text-stone-500 font-bold text-sm">$</span>
                                        <input
                                            type="number"
                                            min="1"
                                            max="1000"
                                            step="1"
                                            value={customTopupValue}
                                            onChange={(e) => setCustomTopupValue(e.target.value)}
                                            placeholder="Enter amount"
                                            className="w-full bg-white dark:bg-stone-800 pl-7 pr-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 text-sm font-mono font-bold text-stone-800 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                                        />
                                    </div>
                                    <span className="text-[10px] text-stone-400 font-medium whitespace-nowrap">USD</span>
                                </div>
                            )}
                        </div>

                        {/* Single Google Pay or Card Checkout Button */}
                        {user.primaryPaymentMethod === 'google-pay' ? (
                            <div className="w-full mb-3">
                                <GooglePayButton
                                    environment="PRODUCTION"
                                    buttonType="pay"
                                    buttonColor={theme === 'dark' ? 'white' : 'black'}
                                    buttonSizeMode="fill"
                                    buttonRadius={10}
                                    className="w-full h-11 shadow-sm"
                                    paymentRequest={{
                                        apiVersion: 2,
                                        apiVersionMinor: 0,
                                        allowedPaymentMethods: [
                                            {
                                                type: 'CARD',
                                                parameters: {
                                                    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                                                    allowedCardNetworks: ['MASTERCARD', 'VISA', 'AMEX', 'DISCOVER', 'JCB'],
                                                },
                                                tokenizationSpecification: {
                                                    type: 'PAYMENT_GATEWAY',
                                                    parameters: {
                                                        gateway: 'stripe',
                                                        'stripe:publishableKey': import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
                                                        'stripe:version': '2020-08-27',
                                                    },
                                                },
                                            },
                                        ],
                                        merchantInfo: {
                                            merchantId: import.meta.env.VITE_GOOGLE_PAY_MERCHANT_ID || '211821118',
                                            merchantName: import.meta.env.VITE_GOOGLE_PAY_MERCHANT_NAME || 'Edgar Cayce AI',
                                        },
                                        transactionInfo: {
                                            totalPriceStatus: 'FINAL',
                                            totalPriceLabel: `$${effectiveTopupAmount.toFixed(2)} AI Quota Top-Up`,
                                            totalPrice: effectiveTopupAmount.toFixed(2),
                                            currencyCode: 'USD',
                                            countryCode: 'US',
                                        },
                                    }}
                                    onLoadPaymentData={paymentRequest => {
                                        console.log('Google Pay top-up success:', paymentRequest);
                                        onTopup(effectiveTopupAmount, 'google-pay');
                                        setSuccessType('topup');
                                        setShowSuccessScreen(true);
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="space-y-2 mb-3">
                                <button
                                    type="button"
                                    onClick={async () => {
                                        if (!user.primaryPaymentMethod) {
                                            setShowPaymentSubpage(true);
                                            return;
                                        }
                                        onTopup(effectiveTopupAmount);
                                    }}
                                    className="w-full py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow transition-all flex items-center justify-center gap-2"
                                >
                                    <i className="fa-solid fa-plus text-xs"></i>
                                    <span>Top Up ${effectiveTopupAmount.toFixed(2)}</span>
                                </button>
                            </div>
                        )}

                        <p className="text-[10px] text-stone-500 italic text-center flex items-center justify-center gap-1.5">
                            {user.primaryPaymentMethod === 'google-pay' ? (
                                <span className="flex items-center gap-1"><GoogleG size={12} /> Fast, direct checkout via Google Pay</span>
                            ) : user.primaryPaymentMethod === 'stripe' ? (
                                <span><i className="fa-brands fa-stripe mr-1"></i> Secure payment via Saved Card</span>
                            ) : (
                                <span><i className="fa-solid fa-lock mr-1"></i> Secure Payment Processing</span>
                            )}
                        </p>
                    </div>
                )}


            </div>
        </div>
        )}

        {/* Billing & Notifications Card */}
        {(!bypassBilling || isEditorMode) && (
        <div id="billing-section" className="bg-white dark:bg-stone-800 rounded-2xl p-6 shadow-sm border border-stone-200 dark:border-stone-700">
            <h3 className="font-bold text-stone-800 dark:text-stone-200 mb-6 flex items-center gap-2">
                <i className="fa-solid fa-credit-card text-amber-500"></i>
                Billing & Payment
            </h3>
            
            <div className="space-y-6">
                  {/* Current Payment Method Display */}
                  <div className="p-4 bg-stone-50 dark:bg-stone-900/50 rounded-xl border border-stone-200 dark:border-stone-700">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center">
                                {user.primaryPaymentMethod === 'google-pay' ? (
                                    <GoogleG size={32} />
                                ) : (
                                    <div className="w-8 h-8 bg-white dark:bg-stone-800 rounded-lg flex items-center justify-center shadow-sm border border-stone-100 dark:border-stone-800">
                                        <i className="fa-solid fa-credit-card text-stone-600 dark:text-stone-400"></i>
                                    </div>
                                )}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-stone-800 dark:text-stone-100 uppercase tracking-widest text-[10px]">Primary Payment Method</p>
                                <p className="text-xs text-stone-500 mt-0.5">
                                    {user.primaryPaymentMethod === 'google-pay' ? 'Google Pay' : (user.primaryPaymentMethod === 'stripe' ? 'Saved Credit Card' : 'None Set')}
                                </p>
                            </div>
                        </div>
                        {user.primaryPaymentMethod ? (
                            <button 
                                onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (window.confirm("Are you sure you want to remove your primary payment method? This will disable automated billing.")) {
                                        try {
                                            console.log("Clearing payment method...");
                                            onPaymentMethodUpdate(null);
                                        } catch (err) {
                                            console.error("Error clearing payment method:", err);
                                        }
                                    }
                                }}
                                className="text-[10px] font-bold text-red-500 hover:text-red-700 dark:hover:text-red-400 uppercase tracking-widest flex items-center gap-1.5 px-3 py-2 bg-stone-100/50 dark:bg-stone-800/50 hover:bg-stone-200/50 dark:hover:bg-stone-700/50 rounded-lg transition-all border border-stone-200 dark:border-stone-700"
                            >
                                <i className="fa-solid fa-trash-can"></i>
                                Remove Method
                            </button>
                        ) : (
                            <div className="flex items-center gap-2 px-3 py-2 bg-stone-100/50 dark:bg-stone-800/50 rounded-lg border border-dashed border-stone-300 dark:border-stone-700">
                                <i className="fa-solid fa-circle-exclamation text-amber-500 text-[10px]"></i>
                                <span className="text-[10px] font-bold text-stone-500 uppercase tracking-widest">Select to Enable Billing</span>
                            </div>
                        )}
                    </div>
                  </div>

                 <button
                    onClick={() => setShowPaymentSubpage(true)}
                    className="w-full py-3 px-4 bg-stone-100 dark:bg-stone-700 hover:bg-stone-200 dark:hover:bg-stone-600 rounded-xl font-bold text-sm text-stone-800 dark:text-stone-200 transition-colors flex items-center justify-center gap-2"
                 >
                    <i className="fa-solid fa-credit-card"></i>
                    Manage Payment Methods
                 </button>


                  <button
                    onClick={() => setShowInvoicesSubpage(true)}
                    className="w-full py-3 px-4 border border-stone-200 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800 rounded-xl font-bold text-sm text-stone-600 dark:text-stone-400 transition-colors flex items-center justify-center gap-2"
                 >
                    <Receipt className="w-4 h-4" />
                    Bills and Payments
                 </button>
            </div>
        </div>
        )}

        {/* Payment Methods Sub-page / Modal */}
        {showPaymentSubpage && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center pt-0 px-4 pb-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                <div className="bg-white dark:bg-stone-900 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                    <div className="p-4 border-b border-stone-100 dark:border-stone-800 flex items-center justify-between bg-stone-50/50 dark:bg-stone-800/20">
                        <h3 className="font-serif font-bold text-stone-800 dark:text-stone-100">Manage Payment Methods</h3>
                        <button 
                            onClick={() => {
                                setShowPaymentSubpage(false);
                                window.location.hash = '';
                            }} 
                            className="w-8 h-8 flex items-center justify-center rounded-full text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition-all"
                        >
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>
                    
                    <div className="p-6 overflow-y-auto space-y-6">
                        <div className="space-y-4">
                            <label className="block text-xs font-bold text-stone-400 uppercase tracking-widest ml-1">Select Mandatory Provider</label>
                            <div className="grid grid-cols-2 gap-3">
                                <button 
                                    onClick={() => setSelectedPaymentMethod('stripe')}
                                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${selectedPaymentMethod === 'stripe' ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 shadow-sm' : 'border-stone-100 dark:border-stone-800 text-stone-500 hover:border-stone-200'}`}
                                >
                                    <div className="w-full flex justify-between items-center px-1 mb-2">
                                        <span className="font-bold text-xs uppercase tracking-wider">Card</span>
                                        <div className={`w-3 h-3 rounded-full border-2 ${selectedPaymentMethod === 'stripe' ? 'border-amber-500 bg-amber-500' : 'border-stone-300'}`} />
                                    </div>
                                    <span className="font-bold text-[10px] uppercase tracking-widest text-stone-400">Stripe / Card</span>
                                </button>
                                <button 
                                    onClick={() => setSelectedPaymentMethod('google-pay')}
                                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-2 ${selectedPaymentMethod === 'google-pay' ? 'border-stone-900 dark:border-white bg-white dark:bg-stone-800 shadow-md ring-2 ring-stone-900/5 dark:ring-white/10' : 'border-stone-100 dark:border-stone-800 text-stone-500 hover:border-stone-200'}`}
                                >
                                    <div className="w-full flex justify-between items-center px-1 mb-2">
                                        <GPayLogo theme={theme} showText={false} />
                                        <div className={`w-3 h-3 rounded-full border-2 ${selectedPaymentMethod === 'google-pay' ? 'border-stone-900 dark:border-stone-100 bg-stone-900 dark:bg-stone-100' : 'border-stone-300'}`} />
                                    </div>
                                    <span className="font-bold text-[10px] uppercase tracking-widest text-stone-400">Google Pay</span>
                                </button>
                            </div>
                        </div>

                        {selectedPaymentMethod === 'stripe' && (
                            <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                                <label className="block text-xs font-bold text-stone-400 uppercase tracking-widest ml-1">Credit / Debit Card</label>
                                <div className="p-4 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl space-y-4">
                                     <div className="relative">
                                         <i className="fa-regular fa-credit-card absolute left-4 top-1/2 -translate-y-1/2 text-stone-400"></i>
                                         <input type="text" placeholder="Card Number" value={stripeCardNum} onChange={e => setStripeCardNum(e.target.value)} className="w-full bg-transparent border-0 outline-none pl-12 py-1 text-sm font-mono text-stone-800 dark:text-stone-100 placeholder:text-stone-300 dark:placeholder:text-stone-600" />
                                     </div>
                                     <div className="grid grid-cols-2 gap-4">
                                         <input type="text" placeholder="MM / YY" value={stripeExpiry} onChange={e => setStripeExpiry(e.target.value)} className="w-full bg-stone-100/50 dark:bg-stone-900/50 p-2 rounded-lg outline-none text-sm font-mono text-center text-stone-800 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-600" />
                                         <input type="text" placeholder="CVC" value={stripeCvc} onChange={e => setStripeCvc(e.target.value)} className="w-full bg-stone-100/50 dark:bg-stone-900/50 p-2 rounded-lg outline-none text-sm font-mono text-center text-stone-800 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-600" />
                                     </div>
                                </div>
                                <p className="text-[10px] text-stone-500 italic text-center">
                                    Your card details are securely handled via Stripe and never stored in our records.
                                </p>
                            </div>
                        )}

                        {selectedPaymentMethod === 'google-pay' && (
                            <div className="p-8 bg-stone-50 dark:bg-stone-800/50 rounded-2xl border-2 border-dashed border-stone-200 dark:border-stone-700 flex flex-col items-center justify-center text-center space-y-4 animate-in zoom-in-95 duration-300">
                                <div className="w-full flex justify-center py-4">
                                    <GooglePayButton
                                        environment="PRODUCTION"
                                        buttonType="pay"
                                        buttonColor={theme === 'dark' ? 'white' : 'black'}
                                        buttonSizeMode="fill"
                                        buttonRadius={100}
                                        className="w-full max-w-[280px] h-12 shadow-lg"
                                        paymentRequest={{
                                            apiVersion: 2,
                                            apiVersionMinor: 0,
                                            allowedPaymentMethods: [
                                                {
                                                    type: 'CARD',
                                                    parameters: {
                                                        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                                                        allowedCardNetworks: ['MASTERCARD', 'VISA', 'AMEX', 'DISCOVER', 'JCB'],
                                                    },
                                                    tokenizationSpecification: {
                                                        type: 'PAYMENT_GATEWAY',
                                                        parameters: {
                                                            gateway: 'stripe',
                                                            'stripe:publishableKey': import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
                                                            'stripe:version': '2020-08-27',
                                                        },
                                                    },
                                                },
                                            ],
                                            merchantInfo: {
                                                merchantId: import.meta.env.VITE_GOOGLE_PAY_MERCHANT_ID || '211821118',
                                                merchantName: import.meta.env.VITE_GOOGLE_PAY_MERCHANT_NAME || 'Edgar Cayce AI',
                                            },
                                            transactionInfo: {
                                                totalPriceStatus: 'FINAL',
                                                totalPriceLabel: 'Total',
                                                totalPrice: '0.00',
                                                currencyCode: 'USD',
                                                countryCode: 'US',
                                            },
                                        }}
                                        onLoadPaymentData={paymentRequest => {
                                            console.log('Google Pay success', paymentRequest?.paymentMethodData?.description || 'Payment authorized');
                                            onPaymentMethodUpdate('google-pay');
                                            setSuccessType('setup');
                                            setShowSuccessScreen(true);
                                            setShowPaymentSubpage(false);
                                            window.location.hash = '';
                                        }}
                                    />
                                </div>
                                <p className="text-xs text-stone-500 mt-2 leading-relaxed">Fast, secure checkout using the payment methods stored in your Google Account.</p>
                            </div>
                        )}
                    </div>

                    {selectedPaymentMethod === 'stripe' && (
                        <div className="p-6 border-t border-stone-100 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-800/20">
                            <button 
                                onClick={() => {
                                    if (!stripeCardNum || !stripeExpiry || !stripeCvc) {
                                        alert("Please enter a valid card number, expiry, and CVC to save this option.");
                                        return;
                                    }
                                    onPaymentMethodUpdate('stripe');
                                    setSuccessType('setup');
                                    setShowSuccessScreen(true);
                                    setShowPaymentSubpage(false);
                                    window.location.hash = '';
                                }}
                                className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold transition-all shadow-lg shadow-amber-500/20"
                            >
                                Save Payment Option
                            </button>
                        </div>
                    )}
                </div>
            </div>
        )}

        {/* Bills and Payments Sub-page */}
        {showInvoicesSubpage && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center pt-0 px-4 pb-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                <div className="bg-white dark:bg-stone-900 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                    <div className="p-4 border-b border-stone-100 dark:border-stone-800 flex items-center justify-between bg-stone-50/50 dark:bg-stone-800/20">
                        <div className="flex items-center gap-3">
                            <button 
                                onClick={() => setShowInvoicesSubpage(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition-all"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <h3 className="font-serif font-bold text-stone-800 dark:text-stone-100">Bills and Payments</h3>
                        </div>
                        <button 
                            onClick={() => setShowInvoicesSubpage(false)} 
                            className="w-8 h-8 flex items-center justify-center rounded-full text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition-all"
                        >
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>

                    <div className="p-6 overflow-y-auto space-y-8 custom-scrollbar">
                        {!selectedInvoice ? (
                            <>
                                {/* Usage Summary Graph */}
                                {!payAsYouGo && (
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-xs font-bold text-stone-400 uppercase tracking-widest">Monthly Usage Trends</h4>
                                            <div className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded flex items-center gap-1">
                                                <Wallet className="w-3 h-3" />
                                                Cap: ${user.monthlySpendCap}
                                            </div>
                                        </div>
                                        <div className="h-48 w-full bg-stone-50 dark:bg-stone-800/50 rounded-2xl p-4 border border-stone-100 dark:border-stone-800">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={usageData}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" opacity={0.5} />
                                                    <XAxis 
                                                        dataKey="month" 
                                                        axisLine={false} 
                                                        tickLine={false} 
                                                        tick={{ fill: '#9ca3af', fontSize: 10, fontWeight: 600 }} 
                                                    />
                                                    <YAxis 
                                                        axisLine={false} 
                                                        tickLine={false} 
                                                        tick={{ fill: '#9ca3af', fontSize: 10, fontWeight: 600 }}
                                                        tickFormatter={(v) => `$${v}`}
                                                    />
                                                    <RechartsTooltip 
                                                        contentStyle={{ 
                                                            backgroundColor: '#ffffff', 
                                                            borderRadius: '12px', 
                                                            border: 'none', 
                                                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                                            fontSize: '11px',
                                                            fontWeight: 'bold'
                                                        }}
                                                        itemStyle={{ color: '#f59e0b' }}
                                                        cursor={{ fill: '#f3f4f6', radius: 4 }}
                                                    />
                                                    <Bar dataKey="usage" radius={[4, 4, 0, 0]}>
                                                        {usageData.map((entry, index) => (
                                                            <Cell 
                                                                key={`cell-${index}`} 
                                                                fill={entry.status === 'paid' ? '#10b981' : (index === usageData.length - 1 ? '#f59e0b' : '#d6d3d1')} 
                                                            />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}

                {/* Payments List */}
                                <div className="space-y-4">
                                    <h4 className="text-xs font-bold text-stone-400 uppercase tracking-widest ml-1">Payment History</h4>
                                    <div className="bg-white dark:bg-stone-900 border border-stone-100 dark:border-stone-800 rounded-2xl overflow-hidden">
                                        {payments.length > 0 ? payments.map((payment, idx) => (
                                            <div 
                                                key={payment.id} 
                                                className={`p-4 flex items-center justify-between hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors ${idx !== payments.length - 1 ? 'border-b border-stone-100 dark:border-stone-800' : ''}`}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${payment.status === 'success' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600' : 'bg-red-50 dark:bg-red-900/20 text-red-600'}`}>
                                                        <DollarSign className="w-5 h-5" />
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-sm text-stone-800 dark:text-stone-100">
                                                            {payment.type === 'topup' ? 'Wallet Top-up' : 'Bill Payment'}
                                                        </p>
                                                        <p className="text-[10px] text-stone-500 font-medium uppercase tracking-wide">
                                                            {payment.date} • {payment.method === 'stripe' ? 'Saved Card' : 'Google Pay'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold text-sm text-stone-900 dark:text-white">${payment.amount.toFixed(2)}</p>
                                                    <p className={`text-[10px] font-bold uppercase tracking-widest ${payment.status === 'success' ? 'text-emerald-500' : 'text-red-500'}`}>
                                                        {payment.status}
                                                    </p>
                                                </div>
                                            </div>
                                        )) : (
                                            <div className="p-8 text-center text-stone-400 text-sm italic">
                                                No payment history found.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Export/Actions */}
                                <div className="flex justify-center pt-2">
                                     <button className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 text-xs font-medium flex items-center gap-2 transition-colors">
                                         <Receipt className="w-3.5 h-3.5" />
                                         Download all invoices as PDF
                                     </button>
                                </div>
                            </>
                        ) : (
                            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                                <button 
                                    onClick={() => setSelectedInvoice(null)}
                                    className="text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 text-xs font-bold flex items-center gap-2 mb-4"
                                >
                                    <ChevronLeft className="w-4 h-4" /> Back to Invoices
                                </button>
                                
                                <div className="bg-stone-50 dark:bg-stone-800/50 border border-stone-200 dark:border-stone-700 p-6 rounded-2xl">
                                    <div className="flex justify-between items-center mb-6">
                                        <div>
                                            <h4 className="text-xl font-bold text-stone-800 dark:text-white">{selectedInvoice.id}</h4>
                                            <p className="text-xs text-stone-500">{selectedInvoice.date}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-4xl font-bold text-amber-500">${selectedInvoice.amount.toFixed(2)}</p>
                                            <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mt-1">Due Amount</p>
                                        </div>
                                    </div>

                                    {/* Daily breakdown graph */}
                                    <div className="space-y-2 mb-8">
                                        <h5 className="text-xs font-bold text-stone-400 uppercase tracking-widest">Daily Spendings & Spend Cap</h5>
                                        <div className="h-48 w-full bg-white dark:bg-stone-900 rounded-xl p-4 border border-stone-100 dark:border-stone-800">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={selectedInvoice.usageDetails || []}>
                                                    <CartesianGrid strokeDasharray="2 2" vertical={false} stroke="#e5e7eb" opacity={0.3} />
                                                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 9 }} tickMargin={5} />
                                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 9 }} tickFormatter={(v) => `$${v}`} />
                                                    <RechartsTooltip 
                                                        contentStyle={{ backgroundColor: '#ffffff', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', fontSize: '11px' }}
                                                        cursor={{ fill: '#f3f4f6' }}
                                                    />
                                                    <Bar dataKey="usage" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <p className="text-[10px] text-stone-500 italic text-center">Graph reflects spend cap raises (e.g. from top-ups) over the billing cycle.</p>
                                    </div>

                                    <div className="border-t border-stone-200 dark:border-stone-700 pt-6">
                                        <h5 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4">Pay via Primary Method</h5>
                                        {user.primaryPaymentMethod === 'google-pay' ? (
                                            <div className="w-full flex">
                                                <GooglePayButton
                                                    environment="PRODUCTION"
                                                    buttonType="pay"
                                                    buttonColor={theme === 'dark' ? 'white' : 'black'}
                                                    buttonSizeMode="fill"
                                                    buttonRadius={100}
                                                    className="w-full h-12 shadow-lg"
                                                    paymentRequest={{
                                                        apiVersion: 2,
                                                        apiVersionMinor: 0,
                                                        allowedPaymentMethods: [{
                                                            type: 'CARD',
                                                            parameters: { allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'], allowedCardNetworks: ['MASTERCARD', 'VISA', 'AMEX', 'DISCOVER', 'JCB'] },
                                                            tokenizationSpecification: { type: 'PAYMENT_GATEWAY', parameters: { gateway: 'stripe', 'stripe:publishableKey': import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '', 'stripe:version': '2020-08-27' } },
                                                        }],
                                                        merchantInfo: { merchantId: import.meta.env.VITE_GOOGLE_PAY_MERCHANT_ID || '211821118', merchantName: import.meta.env.VITE_GOOGLE_PAY_MERCHANT_NAME || 'Edgar Cayce AI' },
                                                        transactionInfo: { totalPriceStatus: 'FINAL', totalPriceLabel: 'Total', totalPrice: selectedInvoice.amount.toFixed(2), currencyCode: 'USD', countryCode: 'US' },
                                                    }}
                                                    onLoadPaymentData={paymentRequest => {
                                                        console.log('Google Pay processing for invoice', paymentRequest);
                                                        if (selectedInvoice) {
                                                            onTopup(selectedInvoice.amount);
                                                            setSuccessType('topup');
                                                            setShowSuccessScreen(true);
                                                            setShowInvoicesSubpage(false);
                                                            setSelectedInvoice(null);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <button 
                                                onClick={() => {
                                                    if (selectedInvoice) {
                                                        onTopup(selectedInvoice.amount);
                                                        setSuccessType('topup');
                                                        setShowSuccessScreen(true);
                                                        setShowInvoicesSubpage(false);
                                                        setSelectedInvoice(null);
                                                    }
                                                }}
                                                className="w-full py-4 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:bg-stone-800 dark:hover:bg-white rounded-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2"
                                            >
                                                <i className="fa-solid fa-credit-card"></i>
                                                1-Click Pay with Saved Card
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}

        {/* Success Screen Modal */}
        {showSuccessScreen && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center pt-0 px-4 pb-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
                <div className="bg-white dark:bg-stone-900 w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 text-center">
                    <div className="p-8 space-y-4">
                        <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/40 rounded-full flex items-center justify-center mx-auto text-emerald-500 animate-bounce">
                            <i className="fa-solid fa-check text-4xl"></i>
                        </div>
                        <h2 className="text-2xl font-serif font-bold text-stone-900 dark:text-white">
                            {successType === 'topup' ? 'Payment Successful!' : 'Method Updated!'}
                        </h2>
                        <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
                            {successType === 'topup' 
                                ? 'Your account has been topped up successfully. Your new quota is now available for use.'
                                : 'Your payment method has been verified and saved. You can now use all premium features.'}
                        </p>
                        <div className="pt-4">
                            <button 
                                onClick={() => setShowSuccessScreen(false)}
                                className="w-full py-3 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 rounded-xl font-bold text-sm hover:scale-[1.02] active:scale-95 transition-all shadow-xl"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}

      </div>
    </div>
  );
};

export default SettingsPage;
